/**
 * Lightweight, framework-agnostic DI container.
 * Token-based (no decorators / reflect-metadata) so it runs identically
 * in browsers, Node and test environments.
 */
export type Token<T> = symbol & { readonly __brand?: T };

export function createToken<T>(description: string): Token<T> {
  return Symbol(description) as Token<T>;
}

export type Factory<T> = (container: Container) => T;

export interface RegisterOptions {
  /** true (default): one instance per container; false: new instance per resolve */
  singleton?: boolean;
}

export class DependencyNotFoundError extends Error {
  constructor(token: symbol) {
    super(`No provider registered for token: ${String(token.description)}`);
    this.name = 'DependencyNotFoundError';
  }
}

export class Container {
  private readonly factories = new Map<symbol, { factory: Factory<unknown>; singleton: boolean }>();
  private readonly instances = new Map<symbol, unknown>();

  constructor(private readonly parent?: Container) {}

  register<T>(token: Token<T>, factory: Factory<T>, options: RegisterOptions = {}): this {
    this.factories.set(token, { factory, singleton: options.singleton ?? true });
    this.instances.delete(token);
    return this;
  }

  registerValue<T>(token: Token<T>, value: T): this {
    return this.register(token, () => value);
  }

  has(token: Token<unknown>): boolean {
    return this.factories.has(token) || (this.parent?.has(token) ?? false);
  }

  resolve<T>(token: Token<T>): T {
    const entry = this.factories.get(token);
    if (!entry) {
      if (this.parent) return this.parent.resolve(token);
      throw new DependencyNotFoundError(token);
    }
    if (!entry.singleton) return entry.factory(this) as T;
    if (!this.instances.has(token)) this.instances.set(token, entry.factory(this));
    return this.instances.get(token) as T;
  }

  /** Child scope: overrides locally, falls back to parent. Ideal for tests & per-request scopes. */
  createScope(): Container {
    return new Container(this);
  }
}
